// Icon resolution for dynamic (admin-managed) feelings & reasons.
//
// Icons rarely change, so we resolve them ONCE at login (when lookups load):
// each icon_url is fetched and converted to a base64 data URI, stored on the
// lookup item as `icon_data`, and cached in localStorage with the lookups.
// Everything downstream (esp. client-generated PDFs) then reads `icon_data`
// from memory — no network requests per report or per download.
//
// @react-pdf/renderer fetches every <Image src> at render time and has no
// onError fallback, so embedding as data URIs up front is what keeps PDF
// generation reliable. When an icon can't be loaded, `icon_data` is null and
// the consumer draws a neutral placeholder instead (no bundled asset).

import { images } from "../assets/styles/Images";

// The bundled fileImage.png, resolved to base64 once and reused as the icon
// placeholder when a dynamic icon is missing/unreachable. It's a same-origin
// bundled asset, so this fetch always succeeds (incl. in dev).
let _placeholderPromise = null;
export function getPlaceholderIconDataUri() {
  if (!_placeholderPromise) {
    _placeholderPromise = fetchIconAsDataUri(images.fileImage).catch(() => null);
  }
  return _placeholderPromise;
}

// Fetch a single image URL and return it as a base64 data URI, or null on failure.
export async function fetchIconAsDataUri(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || !(blob.type || "").startsWith("image")) return null;
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// Resolve a list of lookup items ({...,icon_url}) to include `icon_data` (base64
// or null), reusing a URL-keyed cache so unchanged icons are never re-downloaded.
//
// - URL already in `cache` (resolved earlier)  -> reused, no network.
// - New/changed URL (e.g. backend updated it)  -> fetched fresh.
// - Failed fetch                               -> NOT cached, so it retries on
//   the next login until the icon actually exists.
//
// Returns { items, cache } where `cache` is pruned to only the URLs currently in
// use (so it can't grow unbounded across icon changes). Pass the same cache for
// feelings then reasons and persist the merged result.
export async function attachIconData(items, cache = {}) {
  const list = items || [];
  const urls = [...new Set(list.map((it) => it?.icon_url).filter(Boolean))];
  const nextCache = {};
  const placeholder = await getPlaceholderIconDataUri();

  await Promise.all(
    urls.map(async (url) => {
      if (cache[url]) {
        nextCache[url] = cache[url]; // reuse previously resolved base64
        return;
      }
      const dataUri = await fetchIconAsDataUri(url);
      if (dataUri) nextCache[url] = dataUri; // cache successes only (failures retry next login)
    }),
  );

  // Missing/unreachable icons fall back to the fileImage placeholder (base64).
  const resolvedItems = list.map((it) => ({
    ...it,
    icon_data: (it?.icon_url ? nextCache[it.icon_url] : null) || placeholder || null,
  }));

  return { items: resolvedItems, cache: nextCache };
}

// Synchronously stamp a report payload's dynamic feelings/reasons with the
// already-resolved `icon_data` from the cached lookups (matched by id, then
// code). No network — safe to call on the Download click.
export function attachCachedIcons(reportData, feelingsById = {}, reasonsById = {}, feelingsByCode = {}, reasonsByCode = {}) {
  if (!reportData?.dynamic?.summary) return reportData;
  const clone = JSON.parse(JSON.stringify(reportData));
  const summary = clone.dynamic.summary;

  (summary.feelings || []).forEach((f) => {
    f.icon_data = feelingsById[f.id]?.icon_data ?? feelingsByCode[f.code]?.icon_data ?? null;
  });
  ["negative_reasons", "positive_reasons"].forEach((group) =>
    (summary[group] || []).forEach((r) => {
      r.icon_data = reasonsById[r.id]?.icon_data ?? reasonsByCode[r.code]?.icon_data ?? null;
    }),
  );
  return clone;
}
